// workOrderStatusesContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, doc, addDoc, deleteDoc, getDocs, onSnapshot, setDoc, updateDoc, writeBatch, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { COLORS } from './theme';
import { getWorkflowLayout, saveWorkflowLayout } from './workflowLayoutService';
import { workflowIdForWorkOrder, resolveStatusColorFrom } from './statusColorResolution';

const slugify = (label) => label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'status';

// Structural roles a status can play — separate from its (freely renameable) label, this is
// what lets automatic status changes (assign a tech, mark a job complete, ...) target "whichever
// status means new/active/blocked/etc for this company" instead of a hardcoded literal string.
// Every consumer of this file used to hardcode 'new'/'in progress'/'on hold'/'complete'/'invoiced'
// independently (WorkOrderForm.js's creation flow, WorkOrderDetail.js's/techJobTrackingService.js's
// auto-transition logic, BulkEditWorkOrdersModal.js's own status list, csvImportService.js's
// import validation) — any one of those breaking silently the moment a company customized their
// statuses is exactly the bug this field exists to close.
export const STATUS_CATEGORY = {
  NEW: 'new',
  TRAVELING: 'traveling',
  ACTIVE: 'active',
  BLOCKED: 'blocked',
  COMPLETE: 'complete',
  INVOICED: 'invoiced'
};

// Human-facing labels for the "Technician Action" picker (StatusEditModal.js) — what actually
// happens in the field when a technician taps a button, not the internal category name.
export const STATUS_CATEGORY_LABELS = {
  [STATUS_CATEGORY.NEW]: 'New work order',
  [STATUS_CATEGORY.TRAVELING]: 'Technician starts driving',
  [STATUS_CATEGORY.ACTIVE]: 'Technician arrives / working',
  [STATUS_CATEGORY.COMPLETE]: 'Job marked complete',
  [STATUS_CATEGORY.BLOCKED]: 'Job blocked / incomplete',
  [STATUS_CATEGORY.INVOICED]: 'Invoiced'
};

// The 5 statuses every part of the app used to hard-code independently, in 7 different files
// (WorkOrderList.js, WorkOrderDetail.js, DashboardHome.js, ScheduleAndMap.js, CustomerRecord.js,
// TechnicianWorkOrders.js, AdvancedReportsDashboard.js) — kept here as the one real default so a
// company that's never customized its statuses sees exactly what it always has, and so every
// work order document already storing one of these five strings as its `status` field keeps
// working with zero data migration.
export const DEFAULT_WORK_ORDER_STATUSES = [
  { key: 'new', label: 'New', color: '#FFA500', category: STATUS_CATEGORY.NEW },
  { key: 'in progress', label: 'In Progress', color: COLORS.primary, category: STATUS_CATEGORY.ACTIVE },
  { key: 'on hold', label: 'On Hold', color: COLORS.danger, category: STATUS_CATEGORY.BLOCKED },
  { key: 'complete', label: 'Complete', color: COLORS.success, category: STATUS_CATEGORY.COMPLETE },
  { key: 'invoiced', label: 'Invoiced', color: COLORS.accentPurple, category: STATUS_CATEGORY.INVOICED }
];

// Starter templates (workflow engine Phase 2) — an admin cloning one of these into a new named
// workflow starts from a real, usable shape instead of a blank list. "Recon & Quote Service Flow"
// is modeled directly on Filipp's own hand-drawn flowchart (New -> Recon Needed? -> .../Quote
// Needed? -> .../Ready to Schedule -> Schedule -> Travel Time -> In Progress -> Task Complete? ->
// QA/QC or Reschedule). Colors deliberately vary so a freshly-cloned workflow doesn't render as a
// wall of identical-colored badges. See cloneTemplate() below for how this seeds a real workflow.
export const WORKFLOW_TEMPLATES = [
  {
    id: 'blank',
    name: 'Blank',
    description: 'Start empty and build your own from scratch.',
    statuses: DEFAULT_WORK_ORDER_STATUSES
  },
  {
    id: 'recon-quote-service',
    name: 'Recon & Quote Service Flow',
    description: 'For jobs that may need a site visit and/or a customer-approved quote before work is scheduled.',
    statuses: [
      { key: 'new', label: 'New Work Order', color: '#FFA500', category: STATUS_CATEGORY.NEW,
        transitions: [{ id: 't1', label: 'Continue', targetStatusKey: 'recon_needed' }] },
      { key: 'recon_needed', label: 'Recon Needed?', color: COLORS.primary,
        transitions: [{ id: 't2', label: 'Yes', targetStatusKey: 'schedule_recon' }, { id: 't3', label: 'No', targetStatusKey: 'quote_needed' }] },
      { key: 'schedule_recon', label: 'Schedule Recon', color: COLORS.accentPurple,
        transitions: [{ id: 't4', label: 'Scheduled', targetStatusKey: 'recon_in_progress' }] },
      { key: 'recon_in_progress', label: 'Recon In Progress', color: COLORS.accentPurple,
        transitions: [{ id: 't5', label: 'Complete', targetStatusKey: 'recon_complete' }] },
      { key: 'recon_complete', label: 'Recon Complete?', color: COLORS.primary,
        transitions: [{ id: 't6', label: 'Yes', targetStatusKey: 'quote_needed' }, { id: 't7', label: 'No', targetStatusKey: 'schedule_recon' }] },
      { key: 'quote_needed', label: 'Quote Needed?', color: COLORS.primary,
        transitions: [{ id: 't8', label: 'Yes', targetStatusKey: 'complete_quote' }, { id: 't9', label: 'No', targetStatusKey: 'ready_to_schedule' }] },
      { key: 'complete_quote', label: 'Complete Quote', color: '#E67E22',
        transitions: [{ id: 't10', label: 'Sent', targetStatusKey: 'awaiting_approval' }] },
      { key: 'awaiting_approval', label: 'Awaiting Approval', color: '#E67E22',
        transitions: [{ id: 't11', label: 'Approved', targetStatusKey: 'ready_to_schedule' }] },
      { key: 'ready_to_schedule', label: 'Ready to Schedule', color: '#FFA500',
        transitions: [{ id: 't12', label: 'Schedule', targetStatusKey: 'scheduled' }] },
      { key: 'scheduled', label: 'Scheduled', color: '#FFA500',
        transitions: [{ id: 't13', label: 'En Route', targetStatusKey: 'travel_time' }] },
      { key: 'travel_time', label: 'Travel Time', color: '#3FA0F0', category: STATUS_CATEGORY.TRAVELING,
        transitions: [{ id: 't14', label: 'Arrived', targetStatusKey: 'in_progress' }] },
      { key: 'in_progress', label: 'Task In Progress', color: COLORS.primary, category: STATUS_CATEGORY.ACTIVE,
        transitions: [{ id: 't15', label: 'Done', targetStatusKey: 'task_complete' }] },
      { key: 'task_complete', label: 'Task Complete?', color: COLORS.primary,
        transitions: [{ id: 't16', label: 'Yes', targetStatusKey: 'qa_qc' }, { id: 't17', label: 'No', targetStatusKey: 'reschedule_task' }] },
      { key: 'reschedule_task', label: 'Reschedule Task', color: COLORS.danger,
        transitions: [{ id: 't18', label: 'Rescheduled', targetStatusKey: 'scheduled' }] },
      { key: 'qa_qc', label: 'QA/QC', color: COLORS.success,
        transitions: [{ id: 't19', label: 'Passed', targetStatusKey: 'complete' }] },
      { key: 'complete', label: 'Complete', color: COLORS.success, category: STATUS_CATEGORY.COMPLETE },
      { key: 'invoiced', label: 'Invoiced', color: COLORS.accentPurple, category: STATUS_CATEGORY.INVOICED }
    ]
  },
  {
    id: 'construction-project',
    name: 'Construction Project Flow',
    description: 'For larger jobs with a bid/contract stage and multi-day on-site work.',
    statuses: [
      { key: 'lead', label: 'New Lead', color: '#FFA500', category: STATUS_CATEGORY.NEW,
        transitions: [{ id: 'c1', label: 'Site Visit', targetStatusKey: 'site_visit' }] },
      { key: 'site_visit', label: 'Site Visit', color: COLORS.accentPurple,
        transitions: [{ id: 'c2', label: 'Complete', targetStatusKey: 'bid_prep' }] },
      { key: 'bid_prep', label: 'Bid Preparation', color: '#E67E22',
        transitions: [{ id: 'c3', label: 'Sent', targetStatusKey: 'bid_sent' }] },
      { key: 'bid_sent', label: 'Bid Sent — Awaiting Response', color: '#E67E22',
        transitions: [{ id: 'c4', label: 'Accepted', targetStatusKey: 'contract_signed' }, { id: 'c5', label: 'Declined', targetStatusKey: 'closed_lost' }] },
      { key: 'closed_lost', label: 'Closed — Lost', color: '#999999' },
      { key: 'contract_signed', label: 'Contract Signed', color: COLORS.success,
        transitions: [{ id: 'c6', label: 'Schedule', targetStatusKey: 'scheduled' }] },
      { key: 'scheduled', label: 'Scheduled', color: '#FFA500',
        transitions: [{ id: 'c7', label: 'Start', targetStatusKey: 'in_progress' }] },
      { key: 'in_progress', label: 'Construction In Progress', color: COLORS.primary, category: STATUS_CATEGORY.ACTIVE,
        transitions: [{ id: 'c8', label: 'Substantially Done', targetStatusKey: 'final_walkthrough' }, { id: 'c9', label: 'Blocked', targetStatusKey: 'on_hold' }] },
      { key: 'on_hold', label: 'On Hold', color: COLORS.danger, category: STATUS_CATEGORY.BLOCKED,
        transitions: [{ id: 'c10', label: 'Resume', targetStatusKey: 'in_progress' }] },
      { key: 'final_walkthrough', label: 'Final Walkthrough', color: '#3FA0F0',
        transitions: [{ id: 'c11', label: 'Passed', targetStatusKey: 'complete' }, { id: 'c12', label: 'Punch List', targetStatusKey: 'in_progress' }] },
      { key: 'complete', label: 'Complete', color: COLORS.success, category: STATUS_CATEGORY.COMPLETE },
      { key: 'invoiced', label: 'Invoiced', color: COLORS.accentPurple, category: STATUS_CATEGORY.INVOICED }
    ]
  }
];

// Backfills `category` for any status saved before this field existed. Only ever inferred for
// the 5 original keys (renaming a status's label never changes its key, by design — see
// updateStatus below), so a company that's only ever renamed/recolored the built-ins keeps every
// automatic behavior working exactly as before. A status with a genuinely custom key — one an
// admin added themselves — has no inferred category and simply opts every category-driven
// automation out for that company, rather than guessing wrong and silently moving a work order
// somewhere nobody chose.
//
// Real bug fixed here: this used to check `status.category` (truthy), not whether the field was
// ever actually SET. An admin explicitly clearing a legacy-keyed status's Technician Action
// (picking "No technician action" in StatusEditModal) writes `category: null` — which is
// present, but falsy, so the truthy check treated it exactly like "never set" and silently
// re-inferred it right back to the legacy default on the very next read. From the admin's side
// this looked exactly like "I changed it, hit Save, and it just reverted" — and only ever for
// one of the 5 original keys (new/in progress/on hold/complete/invoiced), since a custom key has
// no entry in KEY_TO_DEFAULT_CATEGORY to revert to. Checking `undefined` instead of truthiness
// distinguishes "this field was never written" (infer the legacy default, unchanged behavior)
// from "this was explicitly set to null" (respect that choice, stop inferring).
const KEY_TO_DEFAULT_CATEGORY = DEFAULT_WORK_ORDER_STATUSES.reduce((acc, s) => {
  acc[s.key] = s.category;
  return acc;
}, {});

// Exported so a plain service module with no hook access of its own (reversalRequestService.js's
// approveReversalRequest — it already does a raw Firestore read for the work order doc itself
// rather than going through this context, since it's called from outside any component tree
// guaranteed to be wrapped in the provider) can resolve one status's category the exact same way
// this context does, without duplicating the inference rule a second time.
export const withInferredCategory = (status) => (
  status.category !== undefined ? status : { ...status, category: KEY_TO_DEFAULT_CATEGORY[status.key] || null }
);

// Whether an assigned technician can see a job once it reaches this status — gates
// TechnicianWorkOrders.js's list, TechDashboard.js's myJobs (and everything downstream of it:
// voice commands, KPI counts), TechnicianSchedule.js's calendar, and
// TechnicianTimeTracking.js's job-linked clock-in picker (see scheduleService.js's
// isJobVisibleToTechnician, the one shared predicate all of those call). Being assigned alone
// was never meant to be enough on its own — see ensureScheduleForAssignment's own comment for the
// history there — this is the status-based half of "released to the technician," on top of (not
// instead of) the existing schedule-link check.
//
// Only ever inferred (never read as a bare falsy check) for a status that's never explicitly set
// this field — same undefined-vs-explicitly-false distinction withInferredCategory's own comment
// explains, so an admin who deliberately unchecks this for a category-having status stays unchecked
// forever, not silently reset back to the inferred default on every read. The inferred default
// itself: visible for anything already understood as a real technician action (driving, arrived/
// working, blocked/incomplete, complete, invoiced) since those are exactly the statuses a
// technician needs to interact with; hidden for New and any custom/no-category planning status
// (recon, quoting, scheduling, ...), so every existing company's workflow starts hiding pre-field
// planning stages from technicians with zero configuration required, matching what a company that
// already uses the category system almost certainly wants — explicitly overridable per status
// either direction via StatusEditModal.js's own checkbox.
const DEFAULT_VISIBLE_CATEGORIES = [
  STATUS_CATEGORY.TRAVELING, STATUS_CATEGORY.ACTIVE, STATUS_CATEGORY.BLOCKED,
  STATUS_CATEGORY.COMPLETE, STATUS_CATEGORY.INVOICED
];

const withInferredVisibility = (status) => (
  status.visibleToTechnician !== undefined
    ? status
    : { ...status, visibleToTechnician: DEFAULT_VISIBLE_CATEGORIES.includes(status.category) }
);

// Same undefined-vs-explicitly-set inference pattern as category/visibility above, applied to
// "wait for every assigned technician" — techJobTrackingService.js's markComplete/markIncomplete
// both read this off the TARGET status. Historically markComplete always waited (hardcoded) and
// markIncomplete never did (no such option existed at all) — inferring true only for Complete and
// false for everything else (including Blocked) preserves both of those exact behaviors for any
// status that's never explicitly touched this field, while making it a real per-status choice for
// Complete AND Blocked once StatusEditModal.js's checkbox (shown on both categories) is used.
const withInferredWaitForAllTechs = (status) => (
  status.waitForAllTechs !== undefined
    ? status
    : { ...status, waitForAllTechs: status.category === STATUS_CATEGORY.COMPLETE }
);

// Hook-free version of the same category-membership test, for the handful of plain service
// modules (workOrderService.js's applyWorkflowTransition) that have no access to
// useWorkOrderStatuses() and are handed an already-resolved status array by their caller instead.
export const statusHasCategory = (statuses, statusKey, category) => (
  !!statusKey && Array.isArray(statuses) && statuses.some((s) => s.key === statusKey && s.category === category)
);

// Hook-free version of getDefaultWorkflowId's own 4-tier resolution below, for the same handful of
// plain service modules (workOrderService.js's createWorkOrder, csvImportService.js) that have no
// access to useWorkOrderStatuses() and previously hardcoded the deterministic `{companyId}_default`
// doc id directly instead of actually resolving it — see those call sites' own comments for the
// real incident that caused (a deleted/renamed workflow that used to occupy that exact id silently
// broke every new work order created through them). Mirrors
// functions/workflowStatusHelpers.js#resolveDefaultWorkflowId, the server-side twin.
export const resolveDefaultWorkflowId = async (companyId) => {
  const byDefaultForSnap = await getDocs(query(collection(db, 'workflows'), where('defaultForCompanyIds', 'array-contains', companyId)));
  if (!byDefaultForSnap.empty) return byDefaultForSnap.docs[0].id;
  const ownedSnap = await getDocs(query(collection(db, 'workflows'), where('companyId', '==', companyId)));
  if (!ownedSnap.empty) {
    const legacyDefault = ownedSnap.docs.find((d) => d.data().isDefault);
    return (legacyDefault || ownedSnap.docs[0]).id;
  }
  return `${companyId}_default`;
};

const WorkOrderStatusesContext = createContext(null);

// Workflow engine Phase 2 (distributed-baking-raven.md) — a company can now have more than one
// named workflow (e.g. "Default"/"Standard Service" plus a separately-shaped "Construction
// Project Flow"), each its own workflows/{id} doc, queried live the same shared-listener shape
// featureFlagsContext.js/skillCategoriesContext.js already use, scoped to the caller's PRIMARY
// company (companyIds[0]) same as those two, not the currently-selected company under
// Multi-Company Support.
//
// Phase 3 (multi-company workflow sharing, distributed-baking-raven.md) — `myCompanyIds` (the
// admin's full company set, optional, defaults to [companyId]) widens the query to also pick up
// workflows shared TO one of those companies (participatingCompanyIds), not just ones owned by
// the primary. Two live listeners merged by doc id, mirroring workOrderService.js's
// subscribeToWorkOrders / customerService.js's subscribeToCustomers exactly (closure-mutated
// per-listener arrays, a readiness-gated emit(), Map dedup, one teardown closure) — the
// established convention for merging two live queries in this codebase, not a new shape.
//
// Backward compatibility for every pre-existing consumer of this context (confirmed ~25 call
// sites across the app as of Phase 2): `statuses` still resolves to the DEFAULT workflow's
// statuses, and getStatusColor/getStatusLabel/getStatusByCategory all take an OPTIONAL trailing
// workflowId — omit it and you get exactly today's single-workflow behavior (the default
// workflow), pass a work order's own wo.workflowId and you get that work order's actual
// workflow's data. This means multi-workflow correctness could be adopted call-site-by-call-site
// rather than needing every consumer touched atomically to avoid breaking the build.
export function WorkOrderStatusesProvider({ companyId, myCompanyIds, children }) {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setWorkflows([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ids = Array.isArray(myCompanyIds) && myCompanyIds.length > 0 ? myCompanyIds : [companyId];
    const workflowsRef = collection(db, 'workflows');

    let primaryDocs = [];
    let participatingDocs = [];
    let primaryReady = false;
    let participatingReady = false;
    const mergeById = (a, b) => {
      const byId = new Map(a.map((d) => [d.id, d]));
      b.forEach((d) => { if (!byId.has(d.id)) byId.set(d.id, d); });
      return Array.from(byId.values());
    };
    const emit = () => {
      if (!primaryReady || !participatingReady) return;
      setWorkflows(mergeById(primaryDocs, participatingDocs));
      setLoading(false);
    };

    const unsubPrimary = onSnapshot(
      query(workflowsRef, where('companyId', 'in', ids)),
      (snap) => { primaryDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() })); primaryReady = true; emit(); },
      (error) => { console.error('Workflows subscription failed:', error); primaryDocs = []; primaryReady = true; emit(); }
    );
    const unsubParticipating = onSnapshot(
      query(workflowsRef, where('participatingCompanyIds', 'array-contains-any', ids)),
      (snap) => { participatingDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() })); participatingReady = true; emit(); },
      (error) => { console.error('Workflows (participating) subscription failed:', error); participatingDocs = []; participatingReady = true; emit(); }
    );

    return () => { unsubPrimary(); unsubParticipating(); };
  }, [companyId, myCompanyIds]);

  // Pure legacy value (the ONLY thing this ever meant before multi-company workflow sharing) —
  // kept under its own name now, used only as getDefaultWorkflowId's tier-4 fallback and by
  // saveWorkflowStatuses' own re-stamping logic below, which needs to track the LEGACY singleton
  // concept specifically, decoupled from the fuller resolution `defaultWorkflowId` now means (see
  // that comment for why conflating the two caused a real bug).
  const legacyDefaultWorkflow = workflows.find((w) => w.isDefault) || workflows[0] || null;
  const legacyDefaultWorkflowId = legacyDefaultWorkflow ? legacyDefaultWorkflow.id : (companyId ? `${companyId}_default` : null);

  // The actual per-company resolver (multi-company workflow sharing, distributed-baking-raven.md).
  // Four tiers, most to least specific:
  //   1. A workflow explicitly marked default FOR this exact company (defaultForCompanyIds) —
  //      set via setWorkflowDefaultForCompany below, the only tier that's genuinely per-company.
  //   2. A workflow OWNED by this company still carrying the legacy singleton isDefault flag —
  //      backward compat for every workflow that predates this feature.
  //   3. Any workflow owned by this company at all (first match) — a company with 2+ workflows
  //      but none explicitly marked default yet still gets a stable, real answer.
  //   4. The provider's own pure legacy fallback (above) — last resort, e.g. for a companyId this
  //      admin doesn't actually belong to, or before workflows[] has loaded at all.
  const getDefaultWorkflowId = (targetCompanyId) => {
    if (!targetCompanyId) return legacyDefaultWorkflowId;
    const explicit = workflows.find((w) => Array.isArray(w.defaultForCompanyIds) && w.defaultForCompanyIds.includes(targetCompanyId));
    if (explicit) return explicit.id;
    const legacy = workflows.find((w) => w.companyId === targetCompanyId && w.isDefault);
    if (legacy) return legacy.id;
    const owned = workflows.find((w) => w.companyId === targetCompanyId);
    if (owned) return owned.id;
    // Tier 3b: a workflow explicitly SHARED to this company. Readable by its users (the workflows
    // read rule has a participatingCompanyIds branch), so it is a legitimate answer.
    const shared = workflows.find((w) => Array.isArray(w.participatingCompanyIds) && w.participatingCompanyIds.includes(targetCompanyId));
    if (shared) return shared.id;

    // Tier 4 is the deterministic `{companyId}_default` id, NOT legacyDefaultWorkflowId.
    //
    // Real bug fixed here: legacyDefaultWorkflowId is `workflows.find(isDefault) || workflows[0]`
    // across EVERY workflow the provider loaded — which for a multi-company admin includes other
    // companies' workflows. So a company with no workflow document of its own was handed another
    // company's, and WorkOrderForm stamps exactly that id onto new work orders. Its own users,
    // who belong only to that company, then cannot read the workflow at all: every status renders
    // as "Needs Attention" and no branch button exists. Returning the deterministic id instead
    // matches resolveDefaultWorkflowId and its server-side twin
    // functions/workflowStatusHelpers.js, and resolves to nothing readable rather than to
    // something belonging to someone else.
    return `${targetCompanyId}_default`;
  };

  // Backward-compat single value — now the FULL 4-tier resolution for the provider's own primary
  // company, not just the legacy tier. Real bug found live: leaving this legacy-only meant an
  // admin explicitly setting a NEW default via the Companies UI never actually took effect for
  // WorkOrderForm.js's very first render (before its own reactive effect could correct it) or for
  // SetupChecklist.js's sample-data generation — both, like every other existing consumer, only
  // ever read this one bare value. Every one of them now picks up an explicitly-set per-company
  // default automatically, with zero changes needed on their end.
  const defaultWorkflowId = getDefaultWorkflowId(companyId);
  const defaultWorkflow = workflows.find((w) => w.id === defaultWorkflowId) || legacyDefaultWorkflow;

  // Resolves to a specific workflow's statuses (inferred-category, always an array — never null)
  // — falls back to the default workflow's statuses for an unset/unknown workflowId, and to the
  // hardcoded 5 defaults if the company has no workflows doc at all yet (identical fallback
  // behavior to before Phase 2 existed).
  const getStatuses = (workflowId) => {
    const wf = (workflowId && workflows.find((w) => w.id === workflowId)) || defaultWorkflow;
    const raw = wf && Array.isArray(wf.statuses) && wf.statuses.length > 0 ? wf.statuses : DEFAULT_WORK_ORDER_STATUSES;
    return raw.map(withInferredCategory).map(withInferredVisibility).map(withInferredWaitForAllTechs);
  };

  // Backward-compat alias — see the provider's own comment above for why this stays the default
  // workflow specifically rather than becoming an error/undefined now that workflows[] exists.
  const statuses = getStatuses(defaultWorkflowId);

  const saveWorkflowStatuses = async (workflowId, updated) => {
    if (!companyId || !workflowId) return;
    const adminOnlyKeys = updated.filter((s) => s.adminOnly).map((s) => s.key);
    // Deliberately checked against the LEGACY value, not the fuller-resolved defaultWorkflowId
    // above — a workflow that's someone's default purely via the NEW mechanism
    // (defaultForCompanyIds) must never silently gain the legacy isDefault flag back just because
    // its statuses happen to get edited; that would re-introduce the exact dual-signal confusion
    // setWorkflowDefaultForCompany's own legacy-clearing fix (above) exists to prevent.
    const isTheLegacyDefault = workflowId === legacyDefaultWorkflowId && (!legacyDefaultWorkflow || legacyDefaultWorkflow.isDefault);
    // THE WORKFLOW KEEPS ITS OWN OWNER. `companyId` here is the PROVIDER's primary company
    // (App.js passes companyIds[0]), which is only the right answer when the workflow being
    // edited actually belongs to it.
    //
    // Real bug fixed here: this wrote the provider's primary companyId unconditionally, through a
    // setDoc merge, on every save. The provider's listener deliberately loads workflows for ALL of
    // the admin's companies, and WorkOrderStatusSettings' picker lists every one of them — so a
    // multi-company admin who opened a PPP-owned workflow and changed anything at all (add a
    // status, rename one, reorder, edit a branch) silently rewrote that document's owner to CPP.
    // firestore.rules permits it, because the document is still in one of their companies, so
    // there was no error and nothing on screen indicated it had happened. Write access travels
    // with companyId (the workflows update rule is owner-only for everything but the
    // defaultForCompanyIds flag), so PPP's own admins could lose the ability to edit their own
    // workflow.
    //
    // Falls back to the provider's primary only when this workflow isn't in the loaded list at
    // all — i.e. a genuinely new document being created by this very setDoc, which is the one
    // case where there is no existing owner to preserve.
    const existingOwnerId = workflows.find((w) => w.id === workflowId)?.companyId;
    await setDoc(doc(db, 'workflows', workflowId), {
      companyId: existingOwnerId || companyId,
      ...(isTheLegacyDefault ? { name: legacyDefaultWorkflow?.name || 'Default', isDefault: true } : {}),
      statuses: updated,
      adminOnlyKeys,
      updatedAt: Timestamp.now()
    }, { merge: true });
  };

  // Keeps "at most one status per workflow" actually true for the NEW category specifically —
  // instead of StatusEditModal.js's conflictingStatus warning just describing a conflict that
  // gets saved anyway. Scoped to NEW only (not every category): NEW is the one category that's
  // structurally required to be a singleton, since it answers "which status does a new work order
  // start at" (resolveStartingStatusKey) — a work order can only have one starting point. Every
  // other category (Complete, Blocked, Active, Traveling, Invoiced) is legitimately shareable
  // across multiple statuses in a branching workflow — a technician's custom branch button always
  // targets one specific status by key (branch.targetStatusKey), never resolves "the one Complete
  // status" by category lookup, so two parallel branches (e.g. a Recon path and a standard-task
  // path) can both end in their own "Complete"-tagged status without conflict. Enforcing
  // uniqueness there too was over-applying the NEW-specific constraint to categories that were
  // never meant to be singletons — real feedback after it blocked exactly that setup.
  //
  // Called from addStatus/updateStatus specifically (not generically inside saveWorkflowStatuses)
  // because it needs to know WHICH status is the one actively being assigned NEW right now — `key`
  // always wins, whichever OTHER status held it loses it. A generic "first array occurrence wins"
  // version would do the opposite of what the admin just did whenever they're reassigning NEW away
  // from an earlier-positioned status onto a later one.
  //
  // The loser gets an explicit `category: null`, not the field omitted — withInferredCategory
  // treats `category === undefined` as "never set, re-infer the legacy default," so omitting it
  // here would let the old category silently reappear on the very next read for any status still
  // using the original 'new' template key.
  const withExclusiveCategory = (list, key, category) => (
    category === STATUS_CATEGORY.NEW ? list.map((s) => (s.key !== key && s.category === category ? { ...s, category: null } : s)) : list
  );

  const addStatus = async (workflowId, label, color, category) => {
    if (!companyId) return;
    const trimmed = label.trim();
    if (!trimmed) return;
    const current = getStatuses(workflowId);
    let key = slugify(trimmed);
    const existingKeys = new Set(current.map((s) => s.key));
    if (existingKeys.has(key)) {
      let n = 2;
      while (existingKeys.has(`${key}_${n}`)) n++;
      key = `${key}_${n}`;
    }
    const withNew = [...current, { key, label: trimmed, color: color || COLORS.primary, ...(category ? { category } : {}) }];
    await saveWorkflowStatuses(workflowId, withExclusiveCategory(withNew, key, category));
  };

  // key is deliberately never editable once created — it's the literal string stored on every
  // work order's own `status` field, so changing it here would silently orphan every work order
  // already sitting in that status. `updates` can carry any other status field (label/color plus,
  // e.g., adminOnly/requireNote/requirePhoto) — merged in one shot via spread, not restricted to
  // label/color, specifically so a caller editing several fields at once (WorkOrderStatusSettings
  // .js's edit form) does it in a single save. Calling this AND setStatusRequirements back-to-back
  // for the same key used to lose whichever wrote second (both derived from the same stale
  // pre-round-trip closure) — real historical bug, why every one of these still routes through a
  // single saveWorkflowStatuses call per invocation rather than being merged client-side across calls.
  const updateStatus = async (workflowId, key, updates) => {
    const merged = getStatuses(workflowId).map((s) => (s.key === key ? { ...s, ...updates } : s));
    await saveWorkflowStatuses(workflowId, withExclusiveCategory(merged, key, updates.category));
  };

  const removeStatus = async (workflowId, key) => {
    await saveWorkflowStatuses(workflowId, getStatuses(workflowId).filter((s) => s.key !== key));
  };

  // Copies an existing status's own fields (color, guardrails, category) into a brand-new status
  // in the same workflow — starts with zero outgoing branches (transitions) rather than copying
  // the original's, and never copies notification rules (a separate collection, keyed by
  // statusKey) either, both by Filipp's own explicit choice: a duplicate should be a clean,
  // unwired starting point, not something that silently shares behavior/notifications with the
  // status it was copied from until reviewed. Returns the new key so the caller (WorkflowCanvas.js)
  // can place it on the canvas next to the original.
  const duplicateStatus = async (workflowId, key) => {
    const current = getStatuses(workflowId);
    const original = current.find((s) => s.key === key);
    if (!original) return null;

    const existingKeys = new Set(current.map((s) => s.key));
    let newKey = `${key}_copy`;
    let n = 2;
    while (existingKeys.has(newKey)) {
      newKey = `${key}_copy_${n}`;
      n++;
    }

    // NEW is the one structurally-enforced singleton (withExclusiveCategory strips it off every
    // OTHER status the moment it's set) — the ORIGINAL already legitimately holds it, so a
    // duplicate must never inherit it, or saving would silently un-mark the original as this
    // workflow's actual starting status.
    const clonedCategory = original.category === STATUS_CATEGORY.NEW ? null : (original.category ?? null);

    const clone = { ...original, key: newKey, label: `${original.label} (copy)`, category: clonedCategory };
    delete clone.transitions;

    await saveWorkflowStatuses(workflowId, [...current, clone]);
    return newKey;
  };

  // Branching action buttons — e.g. a "Recon Complete?" status carrying transitions
  // [{id, label:'Yes', targetStatusKey:'invoiced'}, {id, label:'No', targetStatusKey:'on hold'}],
  // rendered as buttons on the work order (see WorkOrderDetail.js) instead of the freeform status
  // dropdown having to be used for every guided step. Lives on the status object itself (not a
  // separate collection) since a branch is inherently part of its status's own definition and is
  // always edited alongside it.
  const setStatusTransitions = async (workflowId, key, transitions) => {
    await saveWorkflowStatuses(workflowId, getStatuses(workflowId).map((s) => (s.key === key ? { ...s, transitions } : s)));
  };

  // Per-status guardrails. `adminOnly` is enforced server-side (see firestore.rules' workOrders
  // update rule, keyed off adminOnlyKeys above) and is deliberately only ever offered in the UI
  // for statuses with no `category` — a category status (whichever one currently means
  // ACTIVE/COMPLETE/etc for this company) is exactly what a technician's ordinary field actions
  // (start driving, mark complete, ...) write to directly, so admin-locking one of those would
  // silently break that flow for every technician. `requireNote`/`requirePhoto` have no such
  // restriction (any status, including category ones) since they're enforced client-side as a
  // soft "add this before continuing" gate (WorkOrderDetail.js, JobActionCard.js) rather than a
  // hard write-time deny, so there's no lockout risk from applying them broadly — if anything,
  // gating a technician's "mark complete" tap on a note/photo is the main point.
  const setStatusRequirements = async (workflowId, key, requirements) => {
    await saveWorkflowStatuses(workflowId, getStatuses(workflowId).map((s) => (s.key === key ? { ...s, ...requirements } : s)));
  };

  // Public export of the same write every other mutator above already funnels through — exists
  // for Canvas view's undo/redo (WorkOrderStatusSettings.js), which needs to restore a whole
  // previously-snapshotted `statuses` array verbatim rather than deriving a new one from an
  // add/update/remove-shaped call. Goes through the identical write path as every other mutation,
  // not a special case.
  const overwriteStatuses = async (workflowId, statuses) => {
    await saveWorkflowStatuses(workflowId, statuses);
  };

  const reorderStatuses = async (workflowId, orderedKeys) => {
    const current = getStatuses(workflowId);
    const byKey = new Map(current.map((s) => [s.key, s]));
    await saveWorkflowStatuses(workflowId, orderedKeys.map((k) => byKey.get(k)).filter(Boolean));
  };

  // Creates a genuinely new, independently-named workflow — from a blank/starter template
  // (`options.templateId`, one of WORKFLOW_TEMPLATES) or cloned from one of the company's OWN
  // existing live workflows (`options.cloneWorkflowId`) — the two are mutually exclusive, a real
  // workflow doc id and a static template id are only-coincidentally-non-colliding id spaces so
  // this is deliberately explicit rather than one overloaded string param. Never touches the
  // source/template. Returns the new doc's id so the caller (WorkOrderStatusSettings.js) can
  // immediately switch the picker to it.
  const createWorkflow = async (name, options = {}) => {
    if (!companyId) return null;
    const trimmed = name.trim();
    if (!trimmed) return null;
    const { templateId, cloneWorkflowId } = options;

    let sourceStatuses, description;
    if (cloneWorkflowId) {
      const source = workflows.find((w) => w.id === cloneWorkflowId);
      if (!source) return null;
      sourceStatuses = Array.isArray(source.statuses) ? source.statuses : [];
      description = '';
    } else {
      const template = WORKFLOW_TEMPLATES.find((t) => t.id === templateId) || WORKFLOW_TEMPLATES[0];
      sourceStatuses = template.statuses;
      description = template.description || '';
    }

    // A status with no branches must not carry `transitions` at all — Firestore's addDoc()
    // rejects any field explicitly set to `undefined`, so this can't just spread `s` and
    // overwrite the key with undefined for terminal statuses (Complete, Invoiced, etc.). `key`s
    // and `transitions[].id`s are preserved verbatim (never regenerated) — safe across workflows
    // since both are only ever looked up scoped to a single workflow's own array/doc (confirmed:
    // getStatusColor/getStatusLabel always take workflowId), and preserving them is exactly what
    // lets a cloned layout's positions/edgeLanes (keyed by the same strings) still resolve
    // correctly below.
    const statusesForNew = sourceStatuses.map((s) => {
      const copy = { ...s };
      if (s.transitions) copy.transitions = s.transitions.map((t) => ({ ...t }));
      else delete copy.transitions;
      return copy;
    });
    const adminOnlyKeys = statusesForNew.filter((s) => s.adminOnly).map((s) => s.key);
    // participatingCompanyIds/defaultForCompanyIds always stamped, even for a single-company
    // workflow — same convention as every other multi-company entity (tagGroups, materials,
    // customers, workOrders) — but deliberately NEVER copied from the source when cloning: a
    // duplicate starts scoped to just the admin's own primary company, matching this session's
    // established "duplicate starts as a clean slate" precedent (statuses' own transitions,
    // notification rules) — sharing/defaults are a deliberate choice the admin makes afterward via
    // the new "Companies" UI, not something that silently carries over from what was cloned.
    const ref = await addDoc(collection(db, 'workflows'), {
      companyId, name: trimmed, description, isDefault: false,
      participatingCompanyIds: [companyId], defaultForCompanyIds: [],
      statuses: statusesForNew, adminOnlyKeys, createdAt: Timestamp.now(), updatedAt: Timestamp.now()
    });

    // Clone the source's Canvas layout too, so a forked workflow starts laid out the same way,
    // not just structurally identical — best-effort and silent if the source was never opened in
    // Canvas view (no workflowLayouts doc yet), which just leaves the new workflow to fall
    // through to the existing "no saved layout -> computeAutoLayout on first Canvas open" path,
    // same as any other workflow.
    if (cloneWorkflowId) {
      try {
        const sourceLayout = await getWorkflowLayout(cloneWorkflowId);
        if (sourceLayout?.positions) {
          await saveWorkflowLayout(ref.id, companyId, sourceLayout.positions, sourceLayout.edgeLanes || {});
        }
      } catch {
        // Layout clone is a nice-to-have on top of the real (already-saved) workflow data above —
        // never fail workflow creation itself over it.
      }
    }

    return ref.id;
  };

  // The default workflow can never be deleted — every work order that doesn't carry an explicit
  // workflowId (every one created before Phase 2, plus every future one unless a picker/rule
  // assigns otherwise) implicitly depends on it existing. Extended for multi-company sharing: a
  // workflow that's someone ELSE's explicit default (defaultForCompanyIds, not just the legacy
  // singleton isDefault) is equally protected — deleting it would silently orphan that company.
  // Belt and suspenders alongside the UI-level check (WorkOrderStatusSettings.js hides the Delete
  // button under the same condition) rather than a substitute for it.
  // THROWS rather than returning silently. Real bug fixed here: both guards below returned
  // undefined, and WorkOrderStatusSettings only hides Delete for isDefault/defaultForCompanyIds —
  // not for a workflow that is the company's default purely via tier 3 (first one owned by the
  // company, with neither flag set). So the admin saw the button, confirmed the deletion, and
  // absolutely nothing happened: no error, no removal, no explanation.
  const deleteWorkflow = async (workflowId) => {
    if (!workflowId) throw new Error('No workflow selected to delete.');
    if (workflowId === defaultWorkflowId) {
      throw new Error('This is the default workflow for your company. Set a different workflow as the default first, then delete this one.');
    }
    const target = workflows.find((w) => w.id === workflowId);
    if (target && Array.isArray(target.defaultForCompanyIds) && target.defaultForCompanyIds.length > 0) {
      throw new Error('This workflow is set as the default for at least one company. Change that default first, then delete this one.');
    }
    await deleteDoc(doc(db, 'workflows', workflowId));
    // Best-effort — a workflow with no saved Canvas layout yet (never opened in Canvas view)
    // simply has nothing here to delete, which deleteDoc treats as a no-op, not an error.
    await deleteDoc(doc(db, 'workflowLayouts', workflowId)).catch(() => {});
  };

  // Renames a workflow — the one field List/Canvas view previously had no way to change after
  // creation.
  const renameWorkflow = async (workflowId, newName) => {
    const trimmed = (newName || '').trim();
    if (!workflowId || !trimmed) return;
    await updateDoc(doc(db, 'workflows', workflowId), { name: trimmed, updatedAt: Timestamp.now() });
  };

  // Which companies can see/use this workflow at all (participatingCompanyIds) — always
  // re-includes the workflow's own primary companyId regardless of what's passed, same "primary
  // always included" convention tagGroupService.js's createTagGroup already uses. Any company
  // being REMOVED also loses any default-for claim it had on this workflow in the SAME write — a
  // company can't stay marked as defaulting to a workflow it can no longer even see, and per the
  // firestore.rules update below, only the OWNING company's admin can ever reach this function
  // (unlike setWorkflowDefaultForCompany, which a non-owning participating company's admin can
  // also call for their own company specifically) — so this is the only place that stale flag
  // could otherwise ever get cleaned up.
  const updateWorkflowCompanies = async (workflowId, newParticipatingCompanyIds) => {
    if (!workflowId) return;
    const target = workflows.find((w) => w.id === workflowId);
    if (!target) return;
    const nextParticipating = [...new Set([target.companyId, ...(newParticipatingCompanyIds || [])])];
    const removed = (target.participatingCompanyIds || []).filter((id) => id !== target.companyId && !nextParticipating.includes(id));
    const nextDefaults = (target.defaultForCompanyIds || []).filter((id) => !removed.includes(id));
    await updateDoc(doc(db, 'workflows', workflowId), {
      participatingCompanyIds: nextParticipating,
      defaultForCompanyIds: nextDefaults,
      updatedAt: Timestamp.now()
    });
  };

  // Sets (or clears) whether `targetCompanyId` currently defaults to this workflow — enforces "at
  // most one default per company" by finding every OTHER workflow currently holding that company
  // via a FRESH server query (not the local listener state, which could be stale) and clearing it
  // from all of them in the same batch — self-healing if the invariant was ever already violated
  // by anything, not just guarding against this one call. arrayUnion/arrayRemove are
  // server-computed and idempotent, so the actual write can't clobber a concurrent one even though
  // the candidate search itself isn't wrapped in a transaction (the Web SDK's transaction.get()
  // only accepts a DocumentReference, never a Query, so a transaction couldn't protect the part
  // that actually matters — finding every existing holder — anyway).
  const setWorkflowDefaultForCompany = async (workflowId, targetCompanyId, isDefault) => {
    if (!workflowId || !targetCompanyId) return;
    const batch = writeBatch(db);
    const now = Timestamp.now();

    if (isDefault) {
      // Clears BOTH mechanisms a company's default could currently be sitting under: the new
      // defaultForCompanyIds array, or the LEGACY singleton isDefault flag (only ever meaningful
      // for a workflow OWNED by targetCompanyId, since that flag predates any per-company concept
      // at all). Real bug found live: without also clearing the legacy side, a workflow that
      // predates this feature kept showing as a company's default forever, even after a different
      // one was explicitly set — the new mechanism and the old one were never actually exclusive
      // of each other.
      const [byDefaultForSnap, legacySnap] = await Promise.all([
        getDocs(query(collection(db, 'workflows'), where('defaultForCompanyIds', 'array-contains', targetCompanyId))),
        getDocs(query(collection(db, 'workflows'), where('companyId', '==', targetCompanyId), where('isDefault', '==', true)))
      ]);
      const holderUpdates = new Map();
      byDefaultForSnap.docs.filter((d) => d.id !== workflowId).forEach((d) => {
        holderUpdates.set(d.id, { ...(holderUpdates.get(d.id) || {}), defaultForCompanyIds: arrayRemove(targetCompanyId) });
      });
      legacySnap.docs.filter((d) => d.id !== workflowId).forEach((d) => {
        holderUpdates.set(d.id, { ...(holderUpdates.get(d.id) || {}), isDefault: false });
      });
      holderUpdates.forEach((fields, id) => batch.update(doc(db, 'workflows', id), { ...fields, updatedAt: now }));
      batch.update(doc(db, 'workflows', workflowId), { defaultForCompanyIds: arrayUnion(targetCompanyId), updatedAt: now });
    } else {
      // Also clears the legacy flag if THIS exact workflow owns targetCompanyId and still carries
      // it — otherwise "remove default" would silently do nothing visible for a workflow's own
      // primary company, since the legacy flag would keep winning at both display time and
      // getDefaultWorkflowId's own tier-2 resolution.
      const target = workflows.find((w) => w.id === workflowId);
      const updates = { defaultForCompanyIds: arrayRemove(targetCompanyId), updatedAt: now };
      if (target && target.companyId === targetCompanyId && target.isDefault) {
        updates.isDefault = false;
      }
      batch.update(doc(db, 'workflows', workflowId), updates);
    }
    await batch.commit();
  };

  // A statusKey that doesn't match anything currently defined isn't a hypothetical — it happens
  // for real whenever a status gets deleted (or a workflow gets swapped) out from under a work
  // order that's still sitting on it, most often via a branch button whose targetStatusKey no
  // longer exists. Previously this fell back to printing the raw key verbatim (e.g.
  // "recon_needed"), which reads as a broken app rather than a job that needs a human to pick a
  // real status for it. An empty/not-yet-set statusKey (falsy) is left exactly as before —
  // that's a different, legitimate transient state, not an orphaned reference.
  const getStatusColor = (statusKey, workflowId) => (
    resolveStatusColorFrom(statusKey, loading ? null : getStatuses(workflowId), loading)
  );

  /**
   * The workflow a given work order's status must be read against.
   *
   * `wo.workflowId` alone is not enough. A legacy work order has none, and getStatuses then falls
   * back to the default workflow of the PROVIDER's primary company — which, for a job owned by the
   * second company, is the wrong workflow and therefore the wrong colour. The Work Orders list
   * already resolved grouping this way while its own row pills did not, so one screen could show a
   * group header dot and a status pill in two different colours for the same status.
   */
  const resolveWorkflowIdFor = (workOrder) => workflowIdForWorkOrder(workOrder, getDefaultWorkflowId);

  /** Colour and label for a whole work order — the form every screen should use. */
  const getStatusColorFor = (workOrder) => getStatusColor(workOrder && workOrder.status, resolveWorkflowIdFor(workOrder));
  const getStatusLabelFor = (workOrder) => getStatusLabel(workOrder && workOrder.status, resolveWorkflowIdFor(workOrder));

  const getStatusLabel = (statusKey, workflowId) => {
    const match = getStatuses(workflowId).find((s) => s.key === statusKey);
    if (match) return match.label;
    return statusKey ? 'Needs Attention' : statusKey;
  };

  // Shared by getDisambiguatedStatusLabel/getAllDisambiguatedStatusLabels below — maps each status
  // label to the set of workflow ids that use it, so both agree on exactly which labels need a
  // disambiguating suffix.
  const buildStatusLabelWorkflowMap = () => {
    const map = {};
    workflows.forEach((wf) => {
      getStatuses(wf.id).forEach((s) => {
        if (!map[s.label]) map[s.label] = new Set();
        map[s.label].add(wf.id);
      });
    });
    return map;
  };

  // Same as getStatusLabel, but appends "(WorkflowName)" when this status's label is ALSO used by
  // a status in a DIFFERENT workflow — nothing stops two workflows from independently landing on
  // the same label with different underlying keys. Built for src/ReportBuilder.js's status
  // column/filter, which otherwise showed two visually-identical options that actually meant
  // different things depending on which work order's own workflow they came from.
  const getDisambiguatedStatusLabel = (statusKey, workflowId) => {
    const label = getStatusLabel(statusKey, workflowId);
    const map = buildStatusLabelWorkflowMap();
    if ((map[label]?.size || 0) <= 1) return label;
    const wf = (workflowId && workflows.find((w) => w.id === workflowId)) || defaultWorkflow;
    return wf ? `${label} (${wf.name})` : label;
  };

  // The full deduped, disambiguated label set across every workflow's statuses — a report's status
  // filter dropdown options, kept in exact sync with getDisambiguatedStatusLabel's own per-row
  // resolution above (same collision map, so a row's displayed value always matches one real
  // option in the list).
  const getAllDisambiguatedStatusLabels = () => {
    const map = buildStatusLabelWorkflowMap();
    const options = new Set();
    workflows.forEach((wf) => {
      getStatuses(wf.id).forEach((s) => {
        options.add((map[s.label]?.size || 0) > 1 ? `${s.label} (${wf.name})` : s.label);
      });
    });
    return Array.from(options);
  };

  // The key piece for every automatic status change in the app: "whichever status this
  // workflow currently uses to mean X", rather than a literal string. Returns null (not a guess)
  // when no status in the list carries that category — every caller needs to treat that as "this
  // particular automation has nothing to target for this workflow" rather than fall back to a
  // hardcoded key that might not exist in its list at all.
  const getStatusByCategory = (category, workflowId) => getStatuses(workflowId).find((s) => s.category === category) || null;

  // Membership test for "is this status key tagged with this category" — unlike
  // getStatusByCategory (which only ever returns the FIRST matching status, correct for "pick one
  // representative status" uses like resolveStartingStatusKey or a bulk-edit dropdown default),
  // this checks every status in the workflow. Needed anywhere code asks "is this work order
  // complete/invoiced/blocked" rather than "give me a status to assign" — a branching workflow can
  // legitimately have more than one status sharing a category (see withExclusiveCategory's own
  // comment on why that's true for every category except NEW), so a work order sitting on the
  // SECOND Complete-tagged status must still read as complete everywhere that matters: dashboard
  // counts, invoiced-date stamping, technician workload, and closing time segments on a terminal
  // transition. Real bug this fixes: every one of those previously compared a status key against
  // only the first Complete/Invoiced/Blocked status in the list, so a second branch's own
  // Complete-tagged endpoint silently never counted as complete anywhere outside the branch that
  // defined it.
  const isStatusInCategory = (statusKey, category, workflowId) => statusHasCategory(getStatuses(workflowId), statusKey, category);

  // The one safe way to resolve "which status a new work order starts at." getStatusByCategory(NEW)
  // can legitimately return nothing — no status tagged yet, or the original NEW-category status got
  // renamed *and* recategorized away — and every call site used to fall back to the literal string
  // 'new', which may not match any real status key in a fully custom workflow at all (an orphaned
  // work order, status label showing its own raw key forever). Falls back to this workflow's own
  // first status instead — guaranteed to exist, since every workflow has at least one — matching
  // the "starts at this workflow's first status" behavior WorkOrderForm.js has always documented in
  // its own UI copy, just made to actually hold true instead of silently referencing nothing.
  const resolveStartingStatusKey = (workflowId) => getStatusByCategory(STATUS_CATEGORY.NEW, workflowId)?.key || getStatuses(workflowId)[0]?.key;

  return (
    <WorkOrderStatusesContext.Provider value={{
      statuses, loading, workflows, defaultWorkflowId, getDefaultWorkflowId,
      getStatuses, addStatus, updateStatus, removeStatus, reorderStatuses, setStatusTransitions, setStatusRequirements,
      overwriteStatuses, duplicateStatus,
      createWorkflow, deleteWorkflow, renameWorkflow, updateWorkflowCompanies, setWorkflowDefaultForCompany,
      getStatusColor, getStatusLabel, getDisambiguatedStatusLabel, getAllDisambiguatedStatusLabels, getStatusByCategory, isStatusInCategory, resolveStartingStatusKey,
      // Work-order-aware forms. Prefer these over getStatusColor(status, wo.workflowId) — they
      // resolve the right workflow for a legacy job in a secondary company, which the bare pair
      // cannot. See resolveWorkflowIdFor.
      resolveWorkflowIdFor, getStatusColorFor, getStatusLabelFor
    }}>
      {children}
    </WorkOrderStatusesContext.Provider>
  );
}

export function useWorkOrderStatuses() {
  const ctx = useContext(WorkOrderStatusesContext);
  if (!ctx) throw new Error('useWorkOrderStatuses must be used within a WorkOrderStatusesProvider');
  return ctx;
}
